import { useEffect, useRef, useState } from 'react';

/**
 * Lightweight map built on Leaflet + OpenStreetMap tiles, loaded from CDN at runtime (no npm dep).
 * - picker mode: one draggable marker; click/drag/search sets the value
 * - markers mode: read-only pins with popups
 * Geocoding uses OpenStreetMap Nominatim (free, ~1 req/s — fine for a settings form).
 */
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const TILES = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
const BANGKOK: [number, number] = [13.7563, 100.5018];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type L = any;
let loader: Promise<L> | null = null;
export function loadLeaflet(): Promise<L> {
  if (typeof window === 'undefined') return Promise.reject(new Error('ssr'));
  const w = window as unknown as { L?: L };
  if (w.L) return Promise.resolve(w.L);
  if (loader) return loader;
  loader = new Promise<L>((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = LEAFLET_CSS;
      document.head.appendChild(css);
    }
    const s = document.createElement('script');
    s.src = LEAFLET_JS;
    s.async = true;
    s.onload = () => (w.L ? resolve(w.L) : reject(new Error('leaflet failed')));
    s.onerror = () => reject(new Error('โหลดแผนที่ไม่สำเร็จ'));
    document.head.appendChild(s);
  });
  return loader;
}

export async function geocode(q: string): Promise<{ lat: number; lng: number; label: string }[]> {
  const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=5&accept-language=th&q=${encodeURIComponent(q)}`, { headers: { Accept: 'application/json' } });
  if (!r.ok) return [];
  const j = (await r.json()) as { lat: string; lon: string; display_name: string }[];
  return j.map((x) => ({ lat: Number(x.lat), lng: Number(x.lon), label: x.display_name }));
}

export type MapPin = { id: string; lat: number; lng: number; title: string; html?: string; color?: string };

export function LeafletMap({
  value,
  onChange,
  pins,
  height = 280,
  zoom,
  className,
  testId,
}: {
  /** picker: current position (null = none yet) */
  value?: { lat: number; lng: number } | null;
  onChange?: (p: { lat: number; lng: number }) => void;
  /** read-only pins */
  pins?: MapPin[];
  height?: number;
  zoom?: number;
  className?: string;
  testId?: string;
}) {
  const el = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L>(null);
  const markerRef = useRef<L>(null);
  const layerRef = useRef<L>(null);
  const [error, setError] = useState<string | null>(null);
  // Leaflet loads from the CDN asynchronously — the marker/pin effects must run again once the map exists.
  const [ready, setReady] = useState(false);
  const picker = !!onChange;

  // init
  useEffect(() => {
    let dead = false;
    loadLeaflet()
      .then((L) => {
        if (dead || !el.current || mapRef.current) return;
        const center: [number, number] = value ? [value.lat, value.lng] : pins?.length ? [pins[0].lat, pins[0].lng] : BANGKOK;
        const map = L.map(el.current, { center, zoom: zoom ?? (value || pins?.length ? 15 : 11), scrollWheelZoom: true });
        L.tileLayer(TILES, { attribution: ATTR, maxZoom: 19 }).addTo(map);
        mapRef.current = map;
        if (picker) {
          map.on('click', (e: { latlng: { lat: number; lng: number } }) => onChange?.({ lat: +e.latlng.lat.toFixed(6), lng: +e.latlng.lng.toFixed(6) }));
        }
        layerRef.current = L.layerGroup().addTo(map);
        setTimeout(() => map.invalidateSize(), 50);
        setReady(true);
      })
      .catch((e: Error) => setError(e.message));
    return () => {
      dead = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; markerRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // picker marker follows value
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !picker) return;
    const L = (window as unknown as { L: L }).L;
    if (!value) { if (markerRef.current) { markerRef.current.remove(); markerRef.current = null; } return; }
    if (!markerRef.current) {
      markerRef.current = L.marker([value.lat, value.lng], { draggable: true }).addTo(map);
      markerRef.current.on('dragend', () => { const p = markerRef.current.getLatLng(); onChange?.({ lat: +p.lat.toFixed(6), lng: +p.lng.toFixed(6) }); });
      map.setView([value.lat, value.lng], Math.max(map.getZoom(), 15));
    } else {
      markerRef.current.setLatLng([value.lat, value.lng]);
      if (!map.getBounds().contains([value.lat, value.lng])) map.panTo([value.lat, value.lng]);
    }
  }, [value?.lat, value?.lng, picker, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // read-only pins
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer || !pins) return;
    const L = (window as unknown as { L: L }).L;
    layer.clearLayers();
    const pts: [number, number][] = [];
    for (const p of pins) {
      const m = L.circleMarker([p.lat, p.lng], { radius: 8, color: p.color ?? '#4f46e5', fillColor: p.color ?? '#4f46e5', fillOpacity: 0.85, weight: 2 });
      m.bindPopup(p.html ?? `<b>${p.title}</b>`);
      m.bindTooltip(p.title);
      m.addTo(layer);
      pts.push([p.lat, p.lng]);
    }
    if (pts.length > 1) map.fitBounds(pts, { padding: [30, 30], maxZoom: 14 });
    else if (pts.length === 1) map.setView(pts[0], 14);
  }, [pins, ready]);

  // keep the map sized when a dialog animates in
  useEffect(() => {
    const t = setInterval(() => mapRef.current?.invalidateSize(), 400);
    const stop = setTimeout(() => clearInterval(t), 2500);
    return () => { clearInterval(t); clearTimeout(stop); };
  }, []);

  return (
    <div className={className} data-testid={testId}>
      <div ref={el} style={{ height, width: '100%', borderRadius: 12, overflow: 'hidden', background: '#e5e7eb' }} />
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      {picker && !error && <p className="mt-1 text-xs text-gray-500">คลิกบนแผนที่เพื่อปักหมุด · ลากหมุดเพื่อขยับ</p>}
    </div>
  );
}
